import { t } from "i18next";
import * as z from "zod";

import { PAYROLL_FEE_PAYERS } from "@/features/payroll/api/enums";

/**
 * Schémas des formulaires de la paie de masse (payroll-service, guide v4.1
 * §6.11).
 *
 * Factory (règle projet) : créés via `create*Schema()` et mémoïsés au niveau
 * composant sur la langue courante — jamais d'instance exportée figée. Les
 * montants sont saisis en unité MAJEURE et convertis en unité mineure
 * (`majorToMinor`) au moment de construire le payload API (§4.3).
 *
 * Validation `phone` : le guide §4.5 accepte E.164 (`+22462…`), national guinéen
 * (8-9 chiffres), ou format libre normalisé par le serveur. On applique une
 * regex permissive côté front — un refus 400 reste géré par
 * `getBackendErrorMessage`.
 */

const PHONE_REGEXP = /^(?:\+?\d{8,15}|\d{8,9})$/;

const XLSX_MIMES = new Set<string>([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/octet-stream",
]);

const LABEL_MIN = 1;
const LABEL_MAX = 120;

/** Payload d'une ligne (salarié) — commune à create / update / add line. */
export const createPayrollBatchLineSchema = () =>
  z.object({
    employeeName: z
      .string()
      .trim()
      .min(LABEL_MIN, { message: t("requiredFieldError") })
      .max(LABEL_MAX, { message: t("invalidFieldError") }),
    employeeReference: z
      .string()
      .trim()
      .max(80, { message: t("invalidFieldError") })
      .optional()
      .or(z.literal("")),
    phone: z
      .string()
      .trim()
      .regex(PHONE_REGEXP, { message: t("invalidFieldError") }),
    /** Montant brut saisi en unité MAJEURE. Converti via `majorToMinor` au submit. */
    amount: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .positive({ message: t("invalidFieldError") }),
  });

export type PayrollBatchLineFormValues = z.infer<
  ReturnType<typeof createPayrollBatchLineSchema>
>;

/** Création d'une paie (`POST /PayrollBatches/add`). */
export const createPayrollBatchSchema = () => {
  const lineSchema = createPayrollBatchLineSchema();
  return z.object({
    label: z
      .string()
      .trim()
      .min(LABEL_MIN, { message: t("requiredFieldError") })
      .max(LABEL_MAX, { message: t("invalidFieldError") }),
    feePayer: z.enum(PAYROLL_FEE_PAYERS, {
      errorMap: () => ({ message: t("requiredFieldError") }),
    }),
    lines: z
      .array(lineSchema)
      .min(1, { message: t("requiredFieldError") }),
  });
};

export type PayrollBatchFormValues = z.infer<
  ReturnType<typeof createPayrollBatchSchema>
>;

/** Modification d'une paie (`PUT /PayrollBatches/{id}`). */
export const createUpdatePayrollBatchSchema = () =>
  z.object({
    label: z
      .string()
      .trim()
      .min(LABEL_MIN, { message: t("requiredFieldError") })
      .max(LABEL_MAX, { message: t("invalidFieldError") })
      .optional(),
    feePayer: z
      .enum(PAYROLL_FEE_PAYERS, {
        errorMap: () => ({ message: t("invalidFieldError") }),
      })
      .optional(),
  });

export type UpdatePayrollBatchFormValues = z.infer<
  ReturnType<typeof createUpdatePayrollBatchSchema>
>;

/**
 * Import d'un classeur `.xlsx` (`POST /PayrollBatches/import`). La taille max
 * du fichier (10 Mo par défaut côté serveur) est à laisser à la ko-réponse 400
 * si dépassée — on cadre uniquement le MIME côté front.
 */
export const createImportPayrollBatchSchema = () =>
  z.object({
    file: z
      .instanceof(File, { message: t("requiredFieldError") })
      .refine((file) => XLSX_MIMES.has(file.type), {
        message: t("invalidFieldError"),
      }),
    label: z
      .string()
      .trim()
      .min(LABEL_MIN, { message: t("requiredFieldError") })
      .max(LABEL_MAX, { message: t("invalidFieldError") }),
    feePayer: z.enum(PAYROLL_FEE_PAYERS, {
      errorMap: () => ({ message: t("requiredFieldError") }),
    }),
  });

export type ImportPayrollBatchFormValues = z.infer<
  ReturnType<typeof createImportPayrollBatchSchema>
>;
